import React, { useState } from "react";
import {
  Box,
  Typography,
  Paper,
  Divider,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  Button,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Chip,
  useTheme,
  Card,
  CardContent,
  Grid,
} from "@mui/material";
import { format } from "date-fns";
import TimelineIcon from "@mui/icons-material/Timeline";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import SchoolIcon from "@mui/icons-material/School";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";
import SportsFootballIcon from "@mui/icons-material/SportsFootball";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import HourglassEmptyIcon from "@mui/icons-material/HourglassEmpty";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import DescriptionIcon from "@mui/icons-material/Description";
import api from "../../../../utils/api";

const Events = ({ events = [], onUpdateEvent, prospectId }) => {
  const theme = useTheme();
  const [openDialog, setOpenDialog] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [currentEvent, setCurrentEvent] = useState(null);
  const [formData, setFormData] = useState({
    id: null,
    date: new Date(),
    type: "offer",
    school: "",
    status: "pending",
    notes: "",
    amount: "",
    details: "",
  });

  // Sort events by date (most recent first)
  const sortedEvents = [...events].sort(
    (a, b) => new Date(b.date) - new Date(a.date)
  );

  // Handle dialog open for new event
  const handleAddEvent = () => {
    setEditMode(false);
    setFormData({
      id: null,
      date: new Date(),
      type: "offered",
      school: "",
      status: "pending",
      notes: "",
      amount: "",
      details: "",
    });
    setOpenDialog(true);
  };

  // Handle dialog open for edit event
  const handleEditEvent = (event) => {
    setEditMode(true);
    setCurrentEvent(event);
    setFormData({
      id: event.id,
      date: new Date(event.date),
      type: event.type === "offer" ? "offered" : event.type,
      school: event.school,
      status: event.status,
      notes: event.notes || "",
      amount: event.amount || "",
      details: event.details || "",
    });
    setOpenDialog(true);
  };

  // Handle dialog close
  const handleCloseDialog = () => {
    setOpenDialog(false);
  };

  // Handle form field change
  const handleFormChange = (field, value) => {
    setFormData({
      ...formData,
      [field]: value,
    });
  };

  // Handle form submit
  const handleSubmit = async () => {
    try {
      const eventData = {
        ...formData,
        date: formData.date.toISOString(),
      };

      if (editMode) {
        await api.prospects.updateEvent(prospectId, formData.id, eventData);
      } else {
        await api.prospects.addEvent(prospectId, eventData);
      }
      onUpdateEvent(eventData);
      setOpenDialog(false);
    } catch (error) {
      console.error("Error submitting event:", error);
    }
  };

  // Handle delete event
  const handleDeleteEvent = async () => {
    if (window.confirm("Are you sure you want to delete this event?")) {
      try {
        await api.prospects.deleteEvent(prospectId, currentEvent.id);
        onUpdateEvent({
          ...currentEvent,
          deleted: true,
        });
        setOpenDialog(false);
      } catch (error) {
        console.error("Error deleting event:", error);
      }
    }
  };

  // Get icon for event type
  const getEventIcon = (type, status) => {
    switch (type) {
      case "offered":
        return <SchoolIcon color="primary" />;
      case "unofficial_visit":
      case "official_visit":
      case "off_campus_visit":
      case "home_visit":
        return <SportsFootballIcon color="secondary" />;
      case "commitment":
        return <EmojiEventsIcon sx={{ color: theme.palette.success.main }} />;
      case "offer_withdrawal":
      case "decommitted":
        return <CancelIcon color="error" />;
      default:
        return <CalendarTodayIcon />;
    }
  };

  // Get color for event status
  const getStatusColor = (status) => {
    switch (status) {
      case "accepted":
        return theme.palette.success.main;
      case "declined":
        return theme.palette.error.main;
      case "pending":
        return theme.palette.warning.main;
      default:
        return theme.palette.grey[500];
    }
  };

  // Get label for event type
  const getEventTypeLabel = (type) => {
    switch (type) {
      case "offered":
        return "Scholarship Offer";
      case "unofficial_visit":
        return "Unofficial Visit";
      case "official_visit":
        return "Official Visit";
      case "off_campus_visit":
        return "Off Campus Visit";
      case "home_visit":
        return "Home Visit";
      case "commitment":
        return "Commitment";
      case "offer_withdrawal":
        return "Offer Withdrawal";
      case "decommitted":
        return "Decommitted";
      default:
        return "Event";
    }
  };

  // Get status icon
  const getStatusIcon = (status) => {
    switch (status) {
      case "accepted":
        return <CheckCircleIcon sx={{ color: theme.palette.success.main }} />;
      case "declined":
        return <CancelIcon sx={{ color: theme.palette.error.main }} />;
      case "pending":
        return (
          <HourglassEmptyIcon sx={{ color: theme.palette.warning.main }} />
        );
      default:
        return null;
    }
  };

  return (
    <Paper elevation={0} variant="outlined" sx={{ p: 3, borderRadius: 2 }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 2,
        }}
      >
        <Typography
          variant="h6"
          sx={{ fontWeight: "bold", display: "flex", alignItems: "center" }}
        >
          <TimelineIcon sx={{ mr: 1 }} color="primary" />
          Events
        </Typography>
        <Button
          variant="outlined"
          startIcon={<AddIcon />}
          onClick={handleAddEvent}
        >
          Add Event
        </Button>
      </Box>
      <Divider sx={{ mb: 3 }} />

      {sortedEvents.length > 0 ? (
        <Box>
          {/* Summary Cards */}
          <Grid container spacing={2} sx={{ mb: 4 }}>
            <Grid item xs={12} sm={6} md={3}>
              <Card variant="outlined" sx={{ bgcolor: "background.default" }}>
                <CardContent>
                  <Typography
                    variant="subtitle2"
                    color="text.secondary"
                    gutterBottom
                  >
                    Total Offers
                  </Typography>
                  <Typography variant="h4" sx={{ fontWeight: "bold" }}>
                    {events.filter((e) => e.type === "offered").length}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card variant="outlined" sx={{ bgcolor: "background.default" }}>
                <CardContent>
                  <Typography
                    variant="subtitle2"
                    color="text.secondary"
                    gutterBottom
                  >
                    Campus Visits
                  </Typography>
                  <Typography variant="h4" sx={{ fontWeight: "bold" }}>
                    {
                      events.filter((e) =>
                        [
                          "unofficial_visit",
                          "official_visit",
                          "off_campus_visit",
                          "home_visit",
                        ].includes(e.type)
                      ).length
                    }
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card variant="outlined" sx={{ bgcolor: "background.default" }}>
                <CardContent>
                  <Typography
                    variant="subtitle2"
                    color="text.secondary"
                    gutterBottom
                  >
                    Pending Offers
                  </Typography>
                  <Typography
                    variant="h4"
                    sx={{
                      fontWeight: "bold",
                      color: theme.palette.warning.main,
                    }}
                  >
                    {
                      events.filter(
                        (e) => e.type === "offered" && e.status === "pending"
                      ).length
                    }
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card variant="outlined" sx={{ bgcolor: "background.default" }}>
                <CardContent>
                  <Typography
                    variant="subtitle2"
                    color="text.secondary"
                    gutterBottom
                  >
                    Commitment Status
                  </Typography>
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: "bold",
                      color: events.some((e) => e.type === "commitment")
                        ? theme.palette.success.main
                        : theme.palette.grey[500],
                    }}
                  >
                    {events.some((e) => e.type === "commitment")
                      ? "Committed"
                      : "Uncommitted"}
                  </Typography>
                  {events.some((e) => e.type === "commitment") && (
                    <Typography variant="body2" color="text.secondary">
                      to {events.find((e) => e.type === "commitment").school}
                    </Typography>
                  )}
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Timeline */}
          <Stepper orientation="vertical">
            {sortedEvents.map((event, index) => (
              <Step key={event.id} active={true}>
                <StepLabel
                  StepIconComponent={() =>
                    getEventIcon(event.type, event.status)
                  }
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                    }}
                  >
                    <Box>
                      <Typography
                        variant="subtitle1"
                        sx={{ fontWeight: "medium" }}
                      >
                        {event.school}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {getEventTypeLabel(event.type)} •{" "}
                        {format(new Date(event.date), "MMM d, yyyy")}
                      </Typography>
                    </Box>
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                      {event.status && (
                        <Tooltip
                          title={`Status: ${
                            event.status.charAt(0).toUpperCase() +
                            event.status.slice(1)
                          }`}
                        >
                          <Chip
                            icon={getStatusIcon(event.status)}
                            label={
                              event.status.charAt(0).toUpperCase() +
                              event.status.slice(1)
                            }
                            size="small"
                            sx={{
                              bgcolor: `${getStatusColor(event.status)}20`,
                              color: getStatusColor(event.status),
                              mr: 1,
                            }}
                          />
                        </Tooltip>
                      )}
                      <IconButton
                        size="small"
                        onClick={() => handleEditEvent(event)}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  </Box>
                </StepLabel>
                <StepContent>
                  <Box sx={{ ml: 2, mt: 1 }}>
                    {event.amount && (
                      <Typography variant="body2" paragraph>
                        <strong>Amount:</strong> {event.amount}
                      </Typography>
                    )}
                    {event.details && (
                      <Typography variant="body2" paragraph>
                        {event.details}
                      </Typography>
                    )}
                    {event.notes && (
                      <Box
                        sx={{
                          mt: 1,
                          p: 1,
                          bgcolor: "background.default",
                          borderRadius: 1,
                        }}
                      >
                        <Typography variant="body2" color="text.secondary">
                          <DescriptionIcon
                            fontSize="small"
                            sx={{ verticalAlign: "middle", mr: 0.5 }}
                          />
                          Notes: {event.notes}
                        </Typography>
                      </Box>
                    )}
                  </Box>
                </StepContent>
              </Step>
            ))}
          </Stepper>
        </Box>
      ) : (
        <Box sx={{ textAlign: "center", py: 4 }}>
          <TimelineIcon sx={{ fontSize: 60, color: "text.disabled", mb: 2 }} />
          <Typography variant="h6" gutterBottom>
            No Events Yet
          </Typography>
          <Typography variant="body2" color="text.secondary" paragraph>
            Track scholarship offers, campus visits, and commitments here.
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleAddEvent}
          >
            Add First Event
          </Button>
        </Box>
      )}

      {/* Add/Edit Event Dialog */}
      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{editMode ? "Edit Event" : "Add Event"}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Event Date"
                type="date"
                value={
                  typeof formData.date === "string"
                    ? formData.date.substring(0, 10)
                    : format(formData.date, "yyyy-MM-dd")
                }
                onChange={(e) =>
                  handleFormChange("date", new Date(e.target.value))
                }
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Event Type</InputLabel>
                <Select
                  value={formData.type}
                  label="Event Type"
                  onChange={(e) => handleFormChange("type", e.target.value)}
                >
                  <MenuItem value="unofficial_visit">Unofficial Visit</MenuItem>
                  <MenuItem value="official_visit">Official Visit</MenuItem>
                  <MenuItem value="offered">Offered</MenuItem>
                  <MenuItem value="commitment">Commitment</MenuItem>
                  <MenuItem value="offer_withdrawal">Offer Withdrawal</MenuItem>
                  <MenuItem value="decommitted">Decommitted</MenuItem>
                  <MenuItem value="off_campus_visit">Off Campus Visit</MenuItem>
                  <MenuItem value="home_visit">Home Visit</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="School"
                value={formData.school}
                onChange={(e) => handleFormChange("school", e.target.value)}
              />
            </Grid>

            {formData.type === "offered" && (
              <>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Scholarship Amount"
                    value={formData.amount}
                    onChange={(e) => handleFormChange("amount", e.target.value)}
                    placeholder="e.g. Full Ride, Partial, etc."
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <InputLabel>Status</InputLabel>
                    <Select
                      value={formData.status}
                      label="Status"
                      onChange={(e) =>
                        handleFormChange("status", e.target.value)
                      }
                    >
                      <MenuItem value="pending">Pending</MenuItem>
                      <MenuItem value="accepted">Accepted</MenuItem>
                      <MenuItem value="declined">Declined</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
              </>
            )}

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Details"
                value={formData.details}
                onChange={(e) => handleFormChange("details", e.target.value)}
                multiline
                rows={2}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Notes"
                value={formData.notes}
                onChange={(e) => handleFormChange("notes", e.target.value)}
                multiline
                rows={2}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          {editMode && (
            <Button
              onClick={handleDeleteEvent}
              color="error"
              startIcon={<DeleteIcon />}
              sx={{ mr: "auto" }}
            >
              Delete
            </Button>
          )}
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={!formData.school || !formData.date}
          >
            {editMode ? "Update" : "Add"}
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default Events;
